import { useEffect, useState } from "react";
import { Plus, Trash2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api, brl, downloadCsv, formatApiError, STAGES, UNITS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const stageColor = {
  prospeccao: "text-zinc-300 border-zinc-600",
  qualificacao: "text-blue-300 border-blue-500/40",
  proposta: "text-amber-300 border-amber-500/40",
  negociacao: "text-nx-yellow border-nx-yellow/50",
  ganho: "text-green-400 border-green-500/40",
  perdido: "text-red-400 border-red-500/40",
};

const emptyLead = { name: "", company: "", email: "", phone: "", unit: "iatech", value: "", source: "", notes: "" };
const emptyClient = { name: "", company: "", email: "", phone: "", unit: "iatech" };

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const UnitSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
    {Object.entries(UNITS).map(([k, v]) => (
      <option key={k} value={k}>{v}</option>
    ))}
  </select>
);

export default function Crm() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pipeline");
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [clientForm, setClientForm] = useState(emptyClient);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);

  const load = () => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const addLead = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/leads", { ...leadForm, value: parseFloat(leadForm.value) || 0 });
      setLeads((ls) => [data, ...ls]);
      setLeadForm(emptyLead);
      setShowLeadForm(false);
      toast.success("Lead criado com sucesso");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const moveLead = async (leadId, stage) => {
    try {
      await api.patch(`/leads/${leadId}`, { stage });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage } : l)));
      toast.success("Estágio atualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead removido");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const convertToClient = async (lead) => {
    try {
      const { data } = await api.post("/clients", {
        name: lead.name, company: lead.company, email: lead.email, phone: lead.phone, unit: lead.unit,
      });
      await api.patch(`/leads/${lead.id}`, { converted: true });
      setClients((cs) => [data, ...cs]);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, converted: true } : l)));
      toast.success(`${lead.name} convertido em cliente`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addClient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clients", clientForm);
      setClients((cs) => [data, ...cs]);
      setClientForm(emptyClient);
      setShowClientForm(false);
      toast.success("Cliente cadastrado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeClient = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      setClients((cs) => cs.filter((c) => c.id !== id));
      toast.success("Cliente removido");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const tabs = [
    { id: "pipeline", label: "Pipeline" },
    { id: "leads", label: `Leads (${leads.length})` },
    { id: "clientes", label: `Clientes (${clients.length})` },
  ];

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">Comercial</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="crm-title">CRM</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => downloadCsv("/leads/export", "leads.csv")}
            data-testid="crm-export-csv"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
          <button
            onClick={() => setShowLeadForm(true)}
            data-testid="crm-lead-add-button"
            className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo Lead
          </button>
          <button
            onClick={() => setShowClientForm(true)}
            data-testid="crm-client-add-button"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo Cliente
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-zinc-800" data-testid="crm-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`crm-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-nx-yellow text-nx-yellow" : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "pipeline" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="crm-pipeline-kanban">
          {STAGES.map((stage) => {
            const stageLeads = leads.filter((l) => l.stage === stage.id);
            return (
              <div
                key={stage.id}
                className="nx-card p-3 min-h-[280px] flex flex-col"
                data-testid={`kanban-column-${stage.id}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData("leadId");
                  if (id) moveLead(id, stage.id);
                }}
              >
                <div className="flex items-center justify-between px-1 pb-3 border-b border-zinc-800">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{stage.label}</span>
                  <span className="text-[10px] font-mono text-nx-yellow bg-nx-yellow/10 border border-nx-yellow/30 rounded-full px-2 py-0.5">
                    {stageLeads.length}
                  </span>
                </div>
                <div className="mt-3 space-y-3 flex-1">
                  {stageLeads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("leadId", lead.id)}
                      className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3.5 cursor-grab active:cursor-grabbing hover:border-nx-yellow/40 transition-colors"
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <p className="text-sm font-semibold leading-snug">{lead.name}</p>
                      {lead.company && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{lead.company}</p>}
                      <div className="mt-2.5 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-mono text-zinc-500 uppercase">
                          {UNITS[lead.unit]}{lead.source ? ` · ${lead.source}` : ""}
                        </span>
                        <span className="font-mono text-xs font-bold text-nx-yellow">{brl(lead.value)}</span>
                      </div>
                      <select
                        value={lead.stage}
                        onChange={(e) => moveLead(lead.id, e.target.value)}
                        data-testid={`lead-stage-select-${lead.id}`}
                        className="mt-2.5 w-full bg-zinc-800/80 border border-zinc-700 rounded-lg px-2 py-1.5 text-[11px] text-zinc-300 focus:border-nx-yellow focus:outline-none"
                      >
                        {STAGES.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "leads" && (
        <div className="nx-card overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                {["Nome", "Empresa", "Unidade", "Valor", "Estágio", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`lead-row-${lead.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{lead.name}</td>
                  <td className="px-5 py-3.5 text-zinc-400">{lead.company || "—"}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{UNITS[lead.unit]}</td>
                  <td className="px-5 py-3.5 font-mono text-nx-yellow text-xs">{brl(lead.value)}</td>
                  <td className="px-5 py-3.5">
                    <select
                      value={lead.stage}
                      onChange={(e) => moveLead(lead.id, e.target.value)}
                      data-testid={`lead-table-stage-${lead.id}`}
                      className="bg-zinc-800/80 border border-zinc-700 rounded-lg px-2 py-1.5 text-[11px] text-zinc-300 focus:border-nx-yellow focus:outline-none"
                    >
                      {STAGES.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {lead.stage === "ganho" && !lead.converted && (
                        <button
                          onClick={() => convertToClient(lead)}
                          data-testid={`lead-convert-${lead.id}`}
                          className="text-[11px] font-semibold text-nx-yellow hover:underline"
                        >
                          Converter
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button onClick={() => removeLead(lead.id)} data-testid={`lead-delete-${lead.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-500">Nenhum lead cadastrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "clientes" && (
        <div className="nx-card overflow-x-auto" data-testid="clients-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                {["Nome", "Empresa", "Contato", "Unidade", "Status", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{c.name}</td>
                  <td className="px-5 py-3.5 text-zinc-400">{c.company || "—"}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{c.email || c.phone || "—"}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{UNITS[c.unit]}</td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] font-mono uppercase border rounded-full px-2.5 py-1 text-green-400 border-green-500/40">
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    {user?.role === "admin" && (
                      <button onClick={() => removeClient(c.id)} data-testid={`client-delete-${c.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-500">Nenhum cliente cadastrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Novo Lead */}
      {showLeadForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lead-form-modal">
          <form onSubmit={addLead} className="glass rounded-3xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">Novo Lead</h2>
              <button type="button" onClick={() => setShowLeadForm(false)} data-testid="lead-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome *">
                <input required value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} className={inputCls} data-testid="lead-name-input" />
              </Field>
              <Field label="Empresa">
                <input value={leadForm.company} onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })} className={inputCls} data-testid="lead-company-input" />
              </Field>
              <Field label="E-mail">
                <input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className={inputCls} data-testid="lead-email-input" />
              </Field>
              <Field label="Telefone">
                <input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className={inputCls} data-testid="lead-phone-input" />
              </Field>
              <Field label="Unidade">
                <UnitSelect value={leadForm.unit} onChange={(v) => setLeadForm({ ...leadForm, unit: v })} testid="lead-unit-select" />
              </Field>
              <Field label="Valor estimado (R$)">
                <input type="number" min="0" step="0.01" value={leadForm.value} onChange={(e) => setLeadForm({ ...leadForm, value: e.target.value })} className={inputCls} data-testid="lead-value-input" />
              </Field>
              <Field label="Origem">
                <input value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} className={inputCls} placeholder="Instagram, indicação..." data-testid="lead-source-input" />
              </Field>
              <Field label="Observações">
                <input value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} className={inputCls} data-testid="lead-notes-input" />
              </Field>
            </div>
            <button type="submit" data-testid="lead-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              Salvar lead
            </button>
          </form>
        </div>
      )}

      {/* Modal Novo Cliente */}
      {showClientForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="client-form-modal">
          <form onSubmit={addClient} className="glass rounded-3xl p-7 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">Novo Cliente</h2>
              <button type="button" onClick={() => setShowClientForm(false)} data-testid="client-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome *">
                <input required value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} className={inputCls} data-testid="client-name-input" />
              </Field>
              <Field label="Empresa">
                <input value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} className={inputCls} data-testid="client-company-input" />
              </Field>
              <Field label="E-mail">
                <input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} className={inputCls} data-testid="client-email-input" />
              </Field>
              <Field label="Telefone">
                <input value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} className={inputCls} data-testid="client-phone-input" />
              </Field>
              <Field label="Unidade">
                <UnitSelect value={clientForm.unit} onChange={(v) => setClientForm({ ...clientForm, unit: v })} testid="client-unit-select" />
              </Field>
            </div>
            <button type="submit" data-testid="client-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              Salvar cliente
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
